import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Button,
  Input,
  Avatar,
  IconButton,
  Tooltip
} from "@material-tailwind/react";
import Swal from "sweetalert2";
import Select from "react-select";
import { Popover } from "react-tiny-popover";
import moment from "moment";

// Icons
import {
  MagnifyingGlassIcon,
  XCircleIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  TrashIcon,
  InformationCircleIcon
} from "@heroicons/react/24/solid";

// Custom Components
import Toaster from "@/components/Toaster";
import ShowLoader from "@/components/Loader";

// Redux Actions
import { fetchAllUsersLeaves, deleteLeave, fetchLeaveReasons } from "@/store/slices/leaveSlice";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";

// const MONTHS_OPTIONS = [
//   { label: "All Months", value: "All Months" },
//   ...[
//     "January", "February", "March", "April", "May", "June",
//     "July", "August", "September", "October", "November", "December"
//   ].map((m) => ({ label: m, value: m }))
// ];

const currentYear = new Date().getFullYear();
// const YEARS_OPTIONS = [
//   { label: "All Years", value: "All Years" },
//   ...Array.from({ length: 10 }, (_, i) => currentYear - 2 + i).map((y) => ({
//     label: String(y),
//     value: String(y)
//   }))
// ];

export default function UserLeaveManagement() {
  const { t } = useTranslation('leave');
  const dispatch = useDispatch();

  // Redux State - allUsersLeaves now contains Laravel's paginated structure: { data: [...], current_page: 1, last_page: 5, total: 50 }
  const { allUsersLeaves = {}, leaveReasons = [], list_loading, isDeleting } = useSelector((state) => state.leave);
  const leavesList = allUsersLeaves?.data || [];
  const currentPage = allUsersLeaves?.current_page || 1;
  const lastPage = allUsersLeaves?.last_page || 1;
  const totalRecords = allUsersLeaves?.total || 0;
  const hasMore = currentPage < lastPage;

  // Filter & Search State
  const [search, setSearch] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("All Months");
  const [selectedYear, setSelectedYear] = useState("All Years");

  // Modal & Popover State
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState([]);
  const popoverRef = useRef(null);
  const loaderRef = useRef(null); // Reference for infinite scroll target

  // Months Name
    const MONTHS_OPTIONS = [
    { label: t("allMonths"), value: "All Months" },

    ...[
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ].map((m) => ({ label: t(`months.${m}`), value: m }))
  ];

    const YEARS_OPTIONS = [
    { label: t("allYears"), value: "All Years" },
    ...Array.from({ length: 10 }, (_, i) => currentYear - 2 + i).map((y) => ({
      label: String(y),
      value: String(y)
    }))
  ];

  useEffect(() => {
    dispatch(fetchLeaveReasons());
  }, [dispatch]);

  // Handle clicking outside action popover
  useEffect(() => {
    function handleClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setIsPopoverOpen([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openActionPopover = (id) => {
    setIsPopoverOpen((prev) => (prev.includes(id) ? [] : [id]));
  };

  // Helper to calculate duration in days
  const getDuration = (from, to) => {
    const start = moment(from);
    const end = moment(to);
    return end.diff(start, "days") + 1;
  };

  // Debounced API Call when Search or Filters Change (Loads Page 1)
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(
        fetchAllUsersLeaves({
          page: 1,
          per_page: 15,
          search,
          month: selectedMonth,
          year: selectedYear,
        })
      );
    }, 400); // 400ms debounce prevents API spamming while typing

    return () => clearTimeout(timer);
  }, [dispatch, search, selectedMonth, selectedYear]);

  // Infinite Scroll Observer: Automatically loads next page when scrolling to bottom
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && !list_loading && hasMore) {
          dispatch(
            fetchAllUsersLeaves({
              page: currentPage + 1,
              per_page: 15,
              search,
              month: selectedMonth,
              year: selectedYear,
            })
          );
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [list_loading, hasMore, currentPage, search, selectedMonth, selectedYear, dispatch]);

  const handleRemoveSearch = () => {
    setSearch("");
  };

  // Delete Leave Application using SweetAlert2
  // const delete_row = (id) => {
  //   setIsPopoverOpen([]);
  //   Swal.fire({
  //     title: "Are you sure?",
  //     text: "You won't be able to revert this!",
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonColor: "#3085d6",
  //     cancelButtonColor: "#d33",
  //     confirmButtonText: "Yes, delete it!"
  //   }).then((result) => {
  //     if (result.isConfirmed) {
  //       dispatch(deleteLeave({ leave_id: id }))
  //         .unwrap()
  //         .then((res) => {
  //           if (res?.status || res) {
  //             Toaster.success("Leave record deleted successfully!");
  //             if (selectedLeave?.id === id) setSelectedLeave(null);
  //             // Reload page 1 after deletion
  //             dispatch(
  //               fetchAllUsersLeaves({
  //                 page: 1,
  //                 per_page: 15,
  //                 search,
  //                 month: selectedMonth,
  //                 year: selectedYear,
  //               })
  //             );
  //           } else {
  //             Toaster.error(res?.message || "Failed to delete leave.");
  //           }
  //         })
  //         .catch((error) => {
  //           console.error("Delete failed:", error);
  //           Toaster.error("Delete failed, please try again.");
  //         });
  //     }
  //   });
  // };

  const delete_row = (id) => {
    setIsPopoverOpen([]);

    Swal.fire({
      title: t("deleteReasonTitle"),
      input: "textarea",
      inputLabel: t("deleteReasonLabel"),
      inputPlaceholder: t("deleteReasonPlaceholder"),
      inputAttributes: {
        maxlength: 100,
        autocapitalize: "off",
        autocorrect: "off",
      },
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("deleteLeaveButton"),
      cancelButtonText: t("cancelButton"),
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return t("reasonRequiredError");
        }

        if (value !== value.trimStart()) {
          return t("reasonStartSpaceError");
        }

        if (value.trim().length < 5) {
          return t("reasonMinLengthError");
        }

        if (value.trim().length > 100) {
          return t("reasonMaxLengthError");
        }

        return null;
      },
      preConfirm: (value) => value.trim(),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          deleteLeave({
            leave_id: id,
            delete_reason: result.value,
          })
        )
          .unwrap()
          .then((res) => {
            if (res?.status || res) {
              Toaster.success(t("deleteSuccess"));

              if (selectedLeave?.id === id) {
                setSelectedLeave(null);
              }

              dispatch(
                fetchAllUsersLeaves({
                  page: 1,
                  per_page: 15,
                  search,
                  month: selectedMonth,
                  year: selectedYear,
                })
              );
            } else {
              Toaster.error(res?.message || t("deleteFailedGeneral"));
            }
          })
          .catch((error) => {
            console.error("Delete failed:", error);
            Toaster.error(t("deleteFailedTryAgain"));
          });
      }
    });
  };

  const renderTableHeader = (headers) => {
    return headers.map((el) => (
      <th key={el} className="border-b border-blue-gray-50 py-3 px-5 text-left">
        <Typography variant="small" className="text-[11px] font-bold text-black-gray">
          {el}
        </Typography>
      </th>
    ));
  };

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8">
        <ScrollToTop />
      {isDeleting && <ShowLoader />}

      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 px-4 md:px-6">
        <div className="w-full md:flex-1">
          <Typography variant="h5" color="blue-gray">
            {t("employeeRecordsTitle", { totalRecords })}
          </Typography>
        </div>

        {/* Search Bar */}
        <div className="w-full md:flex-1 flex justify-center">
          <div className="w-full max-w-md relative">
            <Input
              label={t("searchPlaceholder")}
              className={search ? "pr-10" : ""}
              icon={!search ? <MagnifyingGlassIcon className="h-5 w-5" /> : ""}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IconButton
                variant="text"
                size="sm"
                className="!absolute right-1 top-1"
                onClick={handleRemoveSearch}
              >
                <XCircleIcon className="h-5 w-5 text-red-500" />
              </IconButton>
            )}
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 px-4 md:px-6">
        <div className="flex w-full md:w-auto items-center gap-3 flex-wrap justify-start">
          <div className="w-44">
            <Select
              placeholder={t("selectMonthPlaceholder")}
              value={MONTHS_OPTIONS.find((opt) => opt.value === selectedMonth)}
              onChange={(opt) => setSelectedMonth(opt ? opt.value : "All Months")}
              options={MONTHS_OPTIONS}
              isClearable={false}
            />
          </div>
          <div className="w-36">
            <Select
              placeholder={t("selectYearPlaceholder")}
              value={YEARS_OPTIONS.find((opt) => opt.value === selectedYear)}
              onChange={(opt) => setSelectedYear(opt ? opt.value : "All Years")}
              options={YEARS_OPTIONS}
              isClearable={false}
            />
          </div>
          {(selectedMonth !== "All Months" || selectedYear !== "All Years" || search) && (
            <Button
              variant="outlined"
              size="sm"
              onClick={() => {
                setSelectedMonth("All Months");
                setSelectedYear("All Years");
                setSearch("");
              }}
              className="border-gray-300 text-gray-700 capitalize font-medium py-2"
            >
              {t("resetFiltersButton")}
            </Button>
          )}
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px] table-auto text-left">
          <thead>
            <tr>
              {renderTableHeader([
                t("employeeHeader"),
                t("leaveTypeHeader"),
                t("durationHeader"),
                t("totalDaysHeader"),
                t("commentLabel"),
                t("actionHeader")
              ])}
            </tr>
          </thead>
          <tbody>
            {leavesList.length > 0 ? (
              leavesList.map((leave, key) => {
                const { id, user, first_name, email, leave_from, leave_to, leave_reason_id, description, status } = leave;
                const name = user?.first_name || first_name || "Unknown";
                const empId = user?.email || email || "N/A";
                const avatarSrc = user?.profile_image ? user.profile_image : "/img/photo-gallery.png";
                const reasonText = leaveReasons?.find((r) => r.id === leave_reason_id)?.leave_reason || t("generalLeave");
                const daysCount = getDuration(leave_from, leave_to);

                const className = `py-3 px-4 min-w-[170px] ${
                  key === leavesList.length - 1 ? "" : "border-b border-blue-gray-50"
                }`;

                return (
                  <tr key={`${id}-${key}`}>
                    {/* Employee Info */}
                    <td className={className}>
                      <div className="flex items-center gap-4">
                        <Avatar src={avatarSrc} alt={name} size="sm" variant="rounded" />
                        <div>
                          <Typography variant="small" color="blue-gray" className="font-semibold">
                            {name}
                          </Typography>
                          <Typography className="text-xs text-gray-500">
                            {t("emailIdLabel")} {empId}
                          </Typography>
                        </div>
                      </div>
                    </td>

                    {/* Leave Reason / Type */}
                    <td className={className}>
                      <Typography className="text-xs font-semibold text-blue-gray-600">
                        {reasonText}
                      </Typography>
                    </td>

                    {/* Duration Dates */}
                    <td className={className}>
                      <Typography variant="small" color="blue-gray" className="font-medium">
                        {moment(leave_from).format("DD MMM YYYY")}
                      </Typography>
                      <Typography className="text-xs text-gray-400">
                        {t("to")} {moment(leave_to).format("DD MMM YYYY")}
                      </Typography>
                    </td>

                    {/* Total Days */}
                    <td className={className}>
                      <Typography variant="small" className="font-semibold text-gray-700">
                        {daysCount} {daysCount > 1 ? t("days") : t("day")}
                      </Typography>
                    </td>

                    {/* Truncated Comment */}
                    <td className={className}>
                      <div className="flex items-center gap-1 max-w-[180px] justify-between">
                        <Typography className="text-xs text-gray-600 truncate max-w-[150px]">
                          {description || <span className="italic text-gray-400">{t("noComment")}</span>}
                        </Typography>
                      </div>
                    </td>

                    {/* Status Badge */}
                    {/* <td className={className}>
                      <span
                        className={`inline-flex items-center rounded-xl px-2.5 py-1 text-xs font-medium inset-ring inset-ring-green-600/20 ${
                          status === "pending"
                            ? "bg-yellow-50 text-yellow-800"
                            : status === "approved" || status === "active"
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-700"
                        }`}
                      >
                        {status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "Applied"}
                      </span>
                    </td> */}

                    {/* Action Popover */}
                    <td className={className}>
                      <div className="flex items-center space-x-2">
                        <Popover
                          isOpen={isPopoverOpen.includes(id)}
                          positions={["bottom", "left"]}
                          padding={4}
                          content={
                            <div
                              ref={popoverRef}
                              className="bg-white shadow-xl rounded-md w-36 flex flex-col z-50 border border-gray-100 py-1"
                            >
                              <button
                                className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                onClick={() => {
                                  setSelectedLeave(leave);
                                  setIsPopoverOpen([]);
                                }}
                              >
                                <EyeIcon className="h-4 w-4 text-blue-500" />
                                {t("viewDetails")}
                              </button>
                              <button
                                className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                onClick={() => delete_row(id)}
                              >
                                <TrashIcon className="h-4 w-4 text-red-500" />
                                {t("deleteButton")}
                              </button>
                            </div>
                          }
                        >
                          <IconButton
                            variant="text"
                            color="blue-gray"
                            onClick={() => openActionPopover(id)}
                          >
                            <EllipsisVerticalIcon className="h-6 w-6" />
                          </IconButton>
                        </Popover>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              !list_loading && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    {t("noRecordsMatching")}
                  </td>
                </tr>
              )
            )}

            {/* Infinite Scroll Loader Row / Sentinel Element */}
            <tr ref={loaderRef}>
              <td colSpan={6} className="text-center py-4">
                {list_loading && currentPage > 1 && (
                  <div role="status" className="flex justify-center items-center gap-2 text-gray-500 text-sm">
                    <svg
                      aria-hidden="true"
                      className="w-6 h-6 text-gray-200 animate-spin fill-green-500"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                    <span>{t("loadingMore")}</span>
                  </div>
                )}
                {/* {!hasMore && leavesList.length > 0 && (
                  <span className="text-xs text-gray-400">{t("noMoreRecords")}</span>
                )} */}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Full Comment Details Modal */}
      {selectedLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden animate-fadeIn">
            <div className="flex justify-between items-center bg-green-800 text-white px-6 py-4">
              <Typography variant="h6" color="white">
                {t("leaveDetailsTitle")}
              </Typography>
              <button
                onClick={() => setSelectedLeave(null)}
                className="text-white hover:text-gray-300 font-bold text-xl leading-none"
              >
                &times;
              </button>
            </div>

            <div className="p-6 space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-semibold text-gray-900">{t("employeeLabel")} </span>
                {selectedLeave.user?.first_name || selectedLeave.first_name}
                <span className="text-gray-400 text-xs ml-1">
                  ({t("emailIdLabel")} {selectedLeave.user?.email || selectedLeave.email})
                </span>
              </div>
              <div>
                <span className="font-semibold text-gray-900">{t("durationLabel")} </span>
                {moment(selectedLeave.leave_from).format("DD MMM YYYY")} {t("to")}{" "}
                {moment(selectedLeave.leave_to).format("DD MMM YYYY")}
              </div>
              <div>
                <span className="font-semibold text-gray-900">{t("reasonLabel")} </span>
                {leaveReasons?.find((r) => r.id === selectedLeave.leave_reason_id)?.leave_reason || t("generalLeave")}
              </div>
              <div>
                <span className="font-semibold text-gray-900 block mb-1">{t("commentLabel")}: </span>
                <p className="bg-gray-50 p-3 rounded border border-gray-100 text-gray-600 whitespace-pre-wrap">
                  {selectedLeave.description || t("commentProvided")}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-3 flex justify-end gap-3 border-t border-gray-100">
              <Button
                variant="outlined"
                size="sm"
                onClick={() => setSelectedLeave(null)}
                className="border-gray-300 text-gray-700 capitalize font-medium"
              >
                {t("closeButton")}
              </Button>
              {/* <Button
                size="sm"
                onClick={() => {
                  const idToDelete = selectedLeave.id;
                  setSelectedLeave(null);
                  delete_row(idToDelete);
                }}
                className="bg-red-600 hover:bg-red-700 text-white capitalize font-medium"
              >
                Delete Leave
              </Button> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}